import { Link } from 'react-router-dom'
import { Bell, CheckSquare, Repeat } from 'lucide-react'
import type { SkillBoardCard } from '../../services/skillService'
import { reminderWords } from '../../utils/habitWords'
import { habitReminderInput } from '../../utils/skillWords'
import { dueLine } from '../../utils/taskForm'

/**
 * Hold me to it, on a skill (owner 2026-09-06): "set up on the active items
 * and displayed here". A skill is never reminded about; the tasks and habits
 * under it are. So this block reads what each live item has set — a task's
 * complete-by and its reminders, a habit's follow-through — and links to the
 * page where it is changed. Nothing is set from here.
 */
export function SkillHoldMeToIt({ card, headingClass }: { card: SkillBoardCard | null; headingClass: string }) {
  const allHabits = card?.items.habits || []
  const habits = allHabits.filter((h) => h.live)
  const tasks = card?.items.tasks || []
  const restingOnly = rows0(tasks.length, habits.length, allHabits.length)
  const rows: Array<{ key: string; kind: 'task' | 'habit'; id: number; title: string; words: string | null }> = []
  for (const t of tasks) rows.push({ key: `t${t.id}`, kind: 'task', id: t.id, title: t.title, words: dueLine(t) })
  for (const h of habits) rows.push({ key: `h${h.id}`, kind: 'habit', id: h.id, title: h.title, words: reminderWords(habitReminderInput(h)) })
  const set = rows.filter((r) => r.words)
  const unset = rows.filter((r) => !r.words)
  return (
    <div data-testid="skill-hold">
      <h3 className={headingClass}><Bell className="h-3.5 w-3.5 text-brand" /> Hold me to it</h3>
      {rows.length === 0 ? (
        <p className="mt-1.5 text-sm text-secondary" data-testid="skill-hold-empty">
          {restingOnly ? 'Its habit is paused, so nothing is reminding you right now. Reminders show here from the live tasks and habits under it.' : 'Reminders are set on the tasks and habits under this skill and show up here. Nothing is under it yet.'}
        </p>
      ) : (
        <ul className="mt-1.5 space-y-1.5 text-sm" data-testid="skill-hold-items">
          {set.map((r) => (
            <li key={r.key} className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-muted" aria-hidden="true">{r.kind === 'habit' ? <Repeat className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}</span>
              <span className="min-w-0">
                <Link to={`/${r.kind}s/${r.id}`} className="font-medium text-primary hover:text-brand hover:underline">{r.title}</Link>
                <span className="block text-app-micro text-secondary">{r.words}</span>
              </span>
            </li>
          ))}
          {unset.length > 0 && (
            <li className="text-app-micro text-muted" data-testid="skill-hold-unset">
              {unset.length === 1 ? <>Nothing set yet on <Link to={`/${unset[0].kind}s/${unset[0].id}`} className="text-brand hover:underline">{unset[0].title}</Link>.</> : `Nothing set yet on ${unset.length} of them.`}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default SkillHoldMeToIt

/** True when the only things under the skill are habits that are resting. */
function rows0(tasks: number, liveHabits: number, allHabits: number): boolean {
  return tasks === 0 && liveHabits === 0 && allHabits > 0
}
